import { mergeAttributes, Node, textblockTypeInputRule } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { nanoid } from 'nanoid'
import { MermaidNodeView } from './mermaid-node-view'

export interface MermaidOptions {
  HTMLAttributes: Record<string, any>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mermaid: {
      insertMermaid: (position?: number) => ReturnType
      setMermaidAt: (position: number, code: string) => ReturnType
    }
  }
}

export const inputRegex = /```mermaid[\s\n]$/

export const MermaidExtension = Node.create<MermaidOptions>({
  name: 'mermaid',
  group: 'block',
  content: 'text*',
  marks: '',
  atom: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'mermaid-diagram',
      },
    }
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) =>
          element.getAttribute('data-dg-id') || element.getAttribute('data-id') || '',
        renderHTML: (attributes) =>
          attributes.id
            ? {
                'data-dg-id': attributes.id,
                'data-id': attributes.id,
              }
            : {},
      },
      code: {
        default: '',
        parseHTML: (element) => {
          const codeElement = element.querySelector('code')
          return codeElement?.textContent || ''
        },
        renderHTML: (attributes) => {
          return {
            'data-code': attributes.code,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="mermaid"]',
        getAttrs: (element) => {
          const code = element.getAttribute('data-code') || ''
          const id = element.getAttribute('data-dg-id') || element.getAttribute('data-id') || ''
          return { code, id }
        },
      },
      {
        tag: 'pre',
        getAttrs: (element) => {
          const codeElement = element.querySelector('code.language-mermaid')
          if (!codeElement) return false
          return {
            code: codeElement.textContent || '',
            id: element.getAttribute('data-dg-id') || element.getAttribute('data-id') || '',
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const code = node.attrs.code || ''
    return [
      'pre',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-dg-id': node.attrs.id || undefined,
        'data-id': node.attrs.id || undefined,
      }),
      ['code', { class: 'language-mermaid' }, code],
    ]
  },

  addCommands() {
    return {
      insertMermaid:
        (position) =>
        ({ chain, state }) => {
          const pos = position ?? state.selection.$head.pos
          const defaultCode = 'graph TD\n    A[Start] --> B[End]'
          return chain()
            .insertContentAt(pos, {
              type: this.name,
              attrs: { id: nanoid(10), code: defaultCode },
            })
            .focus()
            .run()
        },

      setMermaidAt:
        (position, code) =>
        ({ chain }) => {
          return chain()
            .setTextSelection({ from: position, to: position })
            .insertContent({
              type: this.name,
              attrs: { id: nanoid(10), code },
            })
            .run()
        },
    }
  },

  addInputRules() {
    return [
      textblockTypeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: () => ({
          id: nanoid(10),
          code: 'graph TD\n    A[Start] --> B[End]',
        }),
      }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(MermaidNodeView)
  },
})
